import React, { useMemo, useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useHideIntercomLauncher } from 'components/reusable/intercom/IntercomLauncher'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Button, ButtonGroup } from 'slab'
import { usePostMemberOnboardingInNetworkVerification } from 'utils/web_api'
import { type MemberOnboardingProvider } from 'utils/web_api.schemas'

import { type StepComponentProps } from '../types'
import { MedicalPlanCard } from './MedicalPlanCard'
import { PageErrorBanner } from './PageErrorBanner'
import { PlanGranularFilters } from './PlanGranularFilters'
import { type MedicalPlanDisplayItem } from '../medicalPlanTypes'
import { useOnboarding } from '../OnboardingContext'
import { useFilteredPlans } from '../pages/MedicalPlansPage'
import { InNetworkPlansCard } from './InNetworkPlansCard/InNetworkPlansCard'
import { InNetworkProviderCard } from './InNetworkProviderCard/InNetworkProviderCard'

export function ProviderInNetworkVerificationTakeover ({
  navigateToPreviousStep,
  navigateToSpecificStep,
  metadata,
}: StepComponentProps): React.JSX.Element {
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>(metadata ? metadata.planIds : [])
  const { onboardingData } = useOnboarding()
  const providers = onboardingData?.provider_preferences
  const [selectedProviderNpis, setSelectedProviderNpis] = useState<number[]>(providers?.map(p => p.npi) ?? [])
  const { mutateAsync: postInNetworkVerification, isPending } = usePostMemberOnboardingInNetworkVerification()
  useHideIntercomLauncher()

  const {
    availablePlans,
    totalPlans,
    planFilters,
    filterNames,
    setPlanFilters,
    medicalPlanLoading,
    medicalPlanError,
  } = useFilteredPlans()
  const city = onboardingData?.member?.city
  const inNetworkProviders = useMemo(() => {
    if (!availablePlans) return []
    return providers?.filter((provider) => {
      return selectedPlanIds.every(planId => {
        const plan = availablePlans.find(p => p.plan.id === planId)
        return plan?.provider_in_network_map?.find((mapProvider) => {
          return mapProvider.npi === provider.npi
        })?.in_network === 'IN_NETWORK'
      })
    })
  }, [providers, selectedPlanIds, availablePlans])
  const selectedPlans = useMemo(() => {
    const result: MedicalPlanDisplayItem[] = []
    selectedPlanIds.forEach(id => {
      const plan = availablePlans.find(p => p.plan.id === id)
      if (plan) result.push(plan)
    })
    return result
  }, [selectedPlanIds, availablePlans])

  const setShowMedicalPlanDetail = (plan: MedicalPlanDisplayItem) => {
    navigateToSpecificStep?.(`medical_plan_details:${plan.plan.id}`)
  }

  const onToggleSelectedPlan = (plan: MedicalPlanDisplayItem, isSelected: boolean) => {
    if (isSelected) {
      if (selectedPlanIds.length >= 3) return
      setSelectedPlanIds([...selectedPlanIds, plan.plan.id])
    } else {
      setSelectedPlanIds(selectedPlanIds.filter(id => id !== plan.plan.id))
    }
  }
  const onRemovePlan = (plan: MedicalPlanDisplayItem) => {
    onToggleSelectedPlan(plan, false)
  }

  const onToggleSelectedProvider = (provider: MemberOnboardingProvider, isSelected: boolean) => {
    if (isSelected) {
      if (selectedProviderNpis.length >= 3) return
      setSelectedProviderNpis([...selectedProviderNpis, provider.npi])
    } else {
      setSelectedProviderNpis(selectedProviderNpis.filter(id => id !== provider.npi))
    }
  }

  const submitInNetworkVerificationRequest = async () => {
    await postInNetworkVerification({
      data: {
        plan_ids: selectedPlanIds,
        provider_npis: selectedProviderNpis
      }
    })
    navigateToSpecificStep?.('medical_plan')
  }

  if (medicalPlanError) {
    return <PageErrorBanner error={medicalPlanError as GrapeAPIErrorType} />
  }

  return (
    <div className='flex fixed inset-0 overflow-x-auto overflow-y-hidden'>
      <div className='w-[13.125rem]' />
      <main className='overflow-y-auto basis-[36rem] shrink-0 grow'>
        <div className='pt-12 pb-10 pr-10 xl:pr-16 pl-6'>
          <div className='flex flex-col max-w-[44rem]'>
            <span className='heading-md text-main font-semi-bold mb-4'>Get help verifying if your providers are in network</span>
            <span className='body-sm text-main mb-4'>
              We share the most up-to-date info available, but provider networks can change frequently.
              Since having your providers in-network matters, we can help you verify!
              Turnaround expectation is 1-3 business days.
            </span>
            <span className='body-sm text-main mb-10'>
              You can also confirm directly with your provider.
            </span>

            <PlanGranularFilters
              carrierFilterNames={filterNames.carriers}
              metalTierFilterNames={filterNames.metalTiers}
              planTypeFilterNames={filterNames.planTypes}
              filters={planFilters}
              setFilters={setPlanFilters}
            />

            <div className='body mb-6 text-gray-800'>
              {totalPlans} medical plan{totalPlans === 1 ? '' : 's'}
              {city ? ` in ${city}` : ''}
            </div>
            <div className='flex flex-col gap-4'>
              {medicalPlanLoading && <LoadingSpinnerCentered />}
              {availablePlans.length > 0 &&
                availablePlans.map((plan) => {
                  return (
                    <MedicalPlanCard
                      key={plan.plan.id}
                      planDisplayItem={plan}
                      setShowMedicalPlanDetail={setShowMedicalPlanDetail}
                      onTogglePlanVerification={onToggleSelectedPlan}
                      selectedPlansForVerification={selectedPlans}
                    />
                  )
                })}
            </div>
          </div>
        </div>
      </main>
      <div className='overflow-y-auto bg-gray-50 shadow-default relative z-10 shrink-1 min-w-[19rem] w-full max-w-[22.5rem] bg-gradient-to-t from-gray-50 to-thatch-100'>
        <div className='h-full flex flex-col justify-between'>
          <div className='pt-12 px-6 flex flex-col gap-6'>
            <div className='body text-main'>
              We’ll check whether the following providers are in-network with your selected plans
            </div>
            <InNetworkProviderCard
              selectedProviderNpis={selectedProviderNpis}
              providers={providers}
              inNetworkProviders={inNetworkProviders}
              onProviderToggle={onToggleSelectedProvider}
            />
            <InNetworkPlansCard
              plans={selectedPlans}
              onRemovePlan={onRemovePlan}
            />
          </div>
          <div className='py-4 px-6 border-t boder-gray-200'>
            <ButtonGroup>
              <Button onClick={() => navigateToPreviousStep?.()} disabled={isPending}>Cancel</Button>
              <Button variant='primary' disabled={isPending} onClick={submitInNetworkVerificationRequest}>Submit</Button>
            </ButtonGroup>
          </div>
        </div>
      </div>
    </div>
  )
}
